import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { connectSocket } from '../../../utils/socket';
import { TASK_TYPES, TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import { ALL_EXPEDIENT_STATUSES, expedientStatusLabel } from '@claimup/shared/constants/expedient';
import { baseAPI } from '../../../config';
import userStore from '../../../store/userStore';

const formatUser = u => {
  const name = [u.username, u.userlastname].filter(Boolean).join(' ').trim();
  return name || u.usermail || u._id;
};

const FilterButtons = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFilter = searchParams.get('filter') || 'all';
  const selectedTaskType = searchParams.get('task_type') || '';
  const selectedAssignedTo = searchParams.get('assigned_to') || '';
  const selectedClientId = searchParams.get('client_id') || '';
  const selectedEstado = searchParams.get('estado') || '';
  const currentUserId = userStore(state => state.user?.id);

  const [counts, setCounts] = useState({ all: 0, reply: 0, mine: 0 });
  const [admins, setAdmins] = useState([]);
  const [clients, setClients] = useState([]);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${baseAPI}/assignments/counts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setCounts({ all: 0, reply: 0, mine: 0, ...response.data });
    } catch (error) {
      console.error('Error fetching assignment counts:', error);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, searchParams]);

  // Load admins and clients for the selects (parallel).
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      // Al admin, el personal de ClaimUp; al gestor, los gestores de su empresa
      // (/users/list es solo del admin y a él le dejaba el filtro vacío).
      axios.get(`${baseAPI}/assignments/team`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${baseAPI}/users/list?role=client,company`, { headers }).catch(() => ({ data: [] })),
    ]).then(([adminsRes, clientsRes]) => {
      setAdmins(Array.isArray(adminsRes.data) ? adminsRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    });
  }, []);

  // Listen for real-time count updates via socket. Refetch through REST so we
  // get the user-specific "mine" count (not part of the broadcast payload).
  useEffect(() => {
    const socket = connectSocket();
    socket.on('connect', () => socket.emit('join_management'));
    socket.on('assignment_counts_update', () => fetchCounts());
    return () => {
      socket.emit('leave_management');
      socket.close();
    };
  }, [fetchCounts]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const handleTabClick = filterKey => {
    const next = new URLSearchParams(searchParams);
    if (filterKey === 'all') next.delete('filter');
    else next.set('filter', filterKey);
    next.delete('page');
    setSearchParams(next);
  };

  const tabs = [
    { key: 'all', label: 'Todos', count: counts.all },
    { key: 'reply', label: 'Responder', count: counts.reply },
    { key: 'mine', label: 'Mis expedientes', count: counts.mine ?? 0, hidden: !currentUserId },
  ].filter(t => !t.hidden);

  return (
    <div className='flex flex-wrap gap-3 items-center'>
      <div className='flex gap-1.5 items-center'>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            className={`px-2.5 py-0.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
              currentFilter === tab.key
                ? 'bg-indigo-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <span>{tab.label}</span>
            <span
              className={`text-xs font-semibold ${
                currentFilter === tab.key ? 'text-indigo-100' : 'text-slate-500'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <div className='flex flex-wrap gap-2 items-center'>
        <select
          value={selectedTaskType}
          onChange={e => updateParam('task_type', e.target.value)}
          className='text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700'
        >
          <option value=''>Tipo: todos</option>
          {TASK_TYPES.map(type => (
            <option key={type} value={type}>
              {TASK_TYPE_LABELS[type] || type}
            </option>
          ))}
        </select>

        <select
          value={selectedAssignedTo}
          onChange={e => updateParam('assigned_to', e.target.value)}
          className='text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700'
        >
          <option value=''>Asignado a: todos</option>
          {admins.map(u => (
            <option key={u._id} value={u._id}>
              {formatUser(u)}
            </option>
          ))}
        </select>

        <select
          value={selectedClientId}
          onChange={e => updateParam('client_id', e.target.value)}
          className='text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700'
        >
          <option value=''>Cliente: todos</option>
          {clients.map(u => (
            <option key={u._id} value={u._id}>
              {formatUser(u)}
            </option>
          ))}
        </select>

        <select
          value={selectedEstado}
          onChange={e => updateParam('estado', e.target.value)}
          className='text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700'
        >
          <option value=''>Estado: todos</option>
          {ALL_EXPEDIENT_STATUSES.map(estado => (
            <option key={estado} value={estado}>
              {expedientStatusLabel(estado)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default FilterButtons;
